import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "neo:drafts:v1";
const DRAFT_TTL = 7 * 24 * 60 * 60 * 1000; // 7 dias
const CLEANUP_INTERVAL = 5 * 60 * 1000; // 5 minutos

const DraftMessagesContext = createContext(null);

const now = () => Date.now();

const makeKey = (scope, id) => {
  if (!scope) return null;
  if (id === undefined || id === null) return null;
  const normalizedId =
    typeof id === "string" || typeof id === "number"
      ? String(id)
      : id?.toString();
  if (!normalizedId) return null;
  return `${scope}:${normalizedId}`;
};

const readStorage = () => {
  if (typeof window === "undefined") {
    return {};
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    if (parsed && typeof parsed === "object") {
      return parsed;
    }
  } catch (err) {
    console.warn("Falha ao ler rascunhos salvos:", err);
  }
  return {};
};

const persistStorage = (drafts) => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
  } catch (err) {
    console.warn("Falha ao salvar rascunhos:", err);
  }
};

const sanitizeDrafts = (drafts) => {
  const current = now();
  let changed = false;
  const sanitized = {};

  Object.entries(drafts || {}).forEach(([key, value]) => {
    if (!value || typeof value !== "object") {
      changed = true;
      return;
    }
    const body = typeof value.body === "string" ? value.body : "";
    if (!body.trim()) {
      changed = true;
      return;
    }
    const updatedAt =
      typeof value.updatedAt === "number" ? value.updatedAt : 0;
    if (!updatedAt || current - updatedAt > DRAFT_TTL) {
      changed = true;
      return;
    }

    sanitized[key] = {
      body,
      updatedAt,
      ...(value.meta ? { meta: value.meta } : {}),
    };
  });

  if (!changed) {
    return drafts || {};
  }

  return sanitized;
};

export const DraftMessagesProvider = ({ children }) => {
  const [drafts, setDrafts] = useState(() => sanitizeDrafts(readStorage()));

  useEffect(() => {
    persistStorage(drafts);
  }, [drafts]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleStorage = (event) => {
      if (event.key !== STORAGE_KEY) {
        return;
      }
      try {
        const parsed = event.newValue ? JSON.parse(event.newValue) : {};
        setDrafts(sanitizeDrafts(parsed));
      } catch {
        setDrafts({});
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setDrafts((prev) => sanitizeDrafts(prev));
    }, CLEANUP_INTERVAL);
    return () => clearInterval(timer);
  }, []);

  const getDraft = useCallback(
    (scope, id) => {
      const key = makeKey(scope, id);
      if (!key) return null;
      const draft = drafts[key];
      if (!draft) return null;
      if (!draft.updatedAt || now() - draft.updatedAt > DRAFT_TTL) {
        return null;
      }
      return draft;
    },
    [drafts]
  );

  const hasDraft = useCallback(
    (scope, id) => {
      const entry = getDraft(scope, id);
      return Boolean(entry?.body?.trim());
    },
    [getDraft]
  );

  const clearDraft = useCallback((scope, id) => {
    const key = makeKey(scope, id);
    if (!key) return;
    setDrafts((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, key)) {
        return prev;
      }
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const saveDraft = useCallback((scope, id, data) => {
    const key = makeKey(scope, id);
    if (!key) return;
    const body = typeof data?.body === "string" ? data.body : "";
    if (!body.trim()) {
      clearDraft(scope, id);
      return;
    }
    setDrafts((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        ...data,
        body,
        updatedAt: now(),
      },
    }));
  }, [clearDraft]);

  const value = useMemo(
    () => ({
      drafts,
      getDraft,
      hasDraft,
      saveDraft,
      clearDraft,
    }),
    [drafts, getDraft, hasDraft, saveDraft, clearDraft]
  );

  return (
    <DraftMessagesContext.Provider value={value}>
      {children}
    </DraftMessagesContext.Provider>
  );
};

export const useDraftMessages = () => {
  const context = useContext(DraftMessagesContext);
  if (!context) {
    throw new Error(
      "useDraftMessages deve ser usado dentro de DraftMessagesProvider"
    );
  }
  return context;
};
